import { isAuriaIntent } from "../domain/intents.js";

export const BREATHING_LIBRARY = Object.freeze({
  1: {
    id: 1,
    name: "Cohérence cardiaque",
    file: "/sounds/respirations/coherence-respi.mp3",
    pattern: "6 s inspire · 6 s expire",
    benefit: "Installer un rythme régulier et apaisant.",
    intent: "relax",
    reason: "Un rythme stable pour laisser la pression redescendre progressivement.",
  },
  2: {
    id: 2,
    name: "Respiration carrée",
    file: "/sounds/respirations/carre-respi.mp3",
    pattern: "4 s · 4 s · 4 s · 4 s",
    benefit: "Retrouver un cadre simple quand les pensées s'éparpillent.",
    intent: "relax",
    reason: "Ses quatre temps réguliers donnent un point d'appui clair au souffle.",
  },
  3: {
    id: 3,
    name: "Soupir physiologique",
    file: "/sounds/respirations/soupir-respi.mp3",
    pattern: "Double inspire · longue expiration",
    benefit: "Relâcher rapidement une tension présente.",
    intent: "relax",
    reason: "Une séquence courte et directe quand vous avez besoin de souffler sans attendre.",
  },
  4: {
    id: 4,
    name: "Longue expiration",
    file: "/sounds/respirations/longue-exp-respi.mp3",
    pattern: "3 s inspire · 7 s expire",
    benefit: "Ralentir progressivement et préparer le repos.",
    intent: "rest",
    reason: "L'expiration plus longue accompagne naturellement le corps vers un rythme plus calme.",
  },
  5: {
    id: 5,
    name: "Respiration 4-7-8",
    file: "/sounds/respirations/4-7-8-respi.mp3",
    pattern: "4 s · 7 s · 8 s",
    benefit: "Créer une transition lente vers le repos.",
    intent: "rest",
    reason: "Son rythme enveloppant aide à quitter progressivement l'agitation de la journée.",
  },
  6: {
    id: 6,
    name: "Respiration alternée",
    file: "/sounds/respirations/nadi-respi.mp3",
    pattern: "Alternance douce des narines",
    benefit: "Apaiser le mental avant un moment de repos.",
    intent: "rest",
    reason: "L'alternance offre une attention simple lorsque le mental continue de tourner.",
  },
  7: {
    id: 7,
    name: "Respiration triangle",
    file: "/sounds/respirations/triangle-respi.mp3",
    pattern: "4 s · 4 s · 8 s",
    benefit: "Rassembler doucement votre attention.",
    intent: "recenter",
    reason: "Ses trois temps donnent une structure nette sans chercher la performance.",
  },
  8: {
    id: 8,
    name: "Souffle dynamique",
    file: "/sounds/respirations/boosteur-respi.mp3",
    pattern: "Rythme vif · pause · reprise",
    benefit: "Sortir du brouillard et retrouver de la présence.",
    intent: "recenter",
    reason: "Une respiration plus vive lorsque votre attention manque d'élan.",
  },
  9: {
    id: 9,
    name: "Grande ventilation",
    file: "/sounds/respirations/grande-ventil-respi.mp3",
    pattern: "Inspire et expire en trois paliers",
    benefit: "Ouvrir le souffle et clarifier l'attention.",
    intent: "recenter",
    reason: "Une respiration ample pour revenir aux sensations et au moment présent.",
  },
});

const BREATHING_BY_STATE = Object.freeze({
  relax: Object.freeze({ low: 3, medium: 2, high: 1 }),
  rest: Object.freeze({ low: 5, medium: 6, high: 4 }),
  recenter: Object.freeze({ low: 8, medium: 9, high: 7 }),
});

function stateBucket(value) {
  if (!Number.isFinite(value)) return "medium";
  if (value <= 3) return "low";
  if (value <= 7) return "medium";
  return "high";
}

export function pickBreathing({
  stateBefore,
  intent,
  preferences = {},
  excludeBreathingIds = [],
  regenerationCount = 0,
}) {
  if (!isAuriaIntent(intent)) return null;

  const bucket = stateBucket(stateBefore);
  const defaultId =
    BREATHING_BY_STATE[intent]?.[bucket];

  /* Une respiration sans fichier audio ne peut pas être jouée : elle ne doit
     jamais être proposée, même comme choix par défaut d'un état. */
  const intentBreathings = Object.values(
    BREATHING_LIBRARY,
  ).filter(
    (breathing) =>
      breathing.intent === intent && Boolean(breathing.file),
  );

  if (!intentBreathings.length) {
    return null;
  }

  const intentIds = intentBreathings.map(
    (breathing) => breathing.id,
  );

  const learnedStats = preferences?.learning?.breathings?.[intent] || {};

  const bestLearned = intentIds
    .map((id) => ({ id, stat: learnedStats[String(id)] || learnedStats[id] }))
    .filter(
      ({ stat }) =>
        Number(stat?.samples) >= 3 &&
        Number(stat?.mean) > 0,
    )
    .sort(
      (a, b) =>
        Number(b.stat.mean) -
        Number(a.stat.mean),
    )[0]?.id;

  const orderedIds = [
    ...new Set(
      [
        bestLearned,
        defaultId,
        ...intentIds,
      ].filter((id) => id != null && intentIds.includes(id)),
    ),
  ];

  const excluded = new Set(
    excludeBreathingIds.map((id) =>
      String(id),
    ),
  );

  const availableIds = orderedIds.filter(
    (id) => !excluded.has(String(id)),
  );

const selectedId =
  availableIds.length > 0
    ? availableIds[0]
    : orderedIds[
        Math.abs(regenerationCount) %
          orderedIds.length
      ];

const selected =
  BREATHING_LIBRARY[selectedId] || null;

  return selected?.intent === intent
    ? selected
    : null;
}

export function getBreathingById(id) {
  return BREATHING_LIBRARY[id] || null;
}
